import { ReactNode } from 'react';
import { Stack, Title, Button, Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { AuthCard } from 'src/auth/components/AuthCard';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useHeroTextColours } from '../heroTextColours';

type WizardShellProps = {
  title: string;
  subtitle?: React.ReactNode;
  onBack?: () => void;
  backLabel?: string;
  /** Omit to render no primary button, for a step whose content is the action. */
  onAction?: () => void;
  actionLabel?: string;
  actionDisabled?: boolean;
  actionLoading?: boolean;
  /** Widens the step for content that needs more room; the card fills it. */
  maxWidth?: number;
  /** Tightens the heading and spacing so tall content still fits one viewport. */
  compact?: boolean;
  children?: ReactNode;
};

const DEFAULT_MAX_WIDTH = 460;
const SECTION_GAP = 48;
const COMPACT_SECTION_GAP = 24;

const containerVariants: Variants = {
  initial: { opacity: 0, x: 20 },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
};

const itemVariants: Variants = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export function WizardShell({
  title,
  subtitle,
  onBack,
  backLabel = 'Back',
  onAction,
  actionLabel = 'Continue',
  actionDisabled,
  actionLoading,
  maxWidth,
  compact,
  children,
}: WizardShellProps) {
  const shouldReduceMotion = useReducedMotion();
  const colours = useHeroTextColours();
  const initial = shouldReduceMotion ? undefined : 'initial';
  const animate = shouldReduceMotion ? undefined : 'animate';
  const exit = shouldReduceMotion ? undefined : 'exit';

  return (
    <motion.div
      variants={containerVariants}
      initial={initial}
      animate={animate}
      exit={exit}
      style={{ width: '100%', maxWidth: maxWidth ?? DEFAULT_MAX_WIDTH }}
    >
      <Stack gap={compact ? COMPACT_SECTION_GAP : SECTION_GAP} align="center" w="100%">
        <motion.div variants={itemVariants} style={{ width: '100%', textAlign: 'center' }}>
          <Stack gap={compact ? 'xs' : 'md'} align="center">
            <Title
              order={1}
              c={colours.title}
              style={{
                fontSize: compact ? '1.75rem' : '2.5rem',
                fontWeight: FONT_WEIGHTS.bold,
                letterSpacing: '-0.02em',
                margin: 0,
              }}
            >
              {title}
            </Title>
            {subtitle && (
              <Text size={compact ? 'md' : 'lg'} c={colours.subtitle}>
                {subtitle}
              </Text>
            )}
          </Stack>
        </motion.div>

        {children && (
          <motion.div variants={itemVariants} style={{ width: '100%' }}>
            <AuthCard w={maxWidth ? '100%' : undefined}>{children}</AuthCard>
          </motion.div>
        )}

        <motion.div variants={itemVariants}>
          <Stack gap="md" align="center">
            {onAction && (
              <Button
                size="xl"
                px={64}
                style={{ borderRadius: 999, height: 64, fontSize: '1.25rem' }}
                styles={{
                  root: {
                    '&:disabled': {
                      backgroundColor: 'var(--mantine-color-gray-8)',
                      color: 'var(--mantine-color-gray-5)',
                    },
                  },
                }}
                rightSection={<IconArrowRight size={24} />}
                onClick={onAction}
                disabled={actionDisabled}
                loading={actionLoading}
              >
                {actionLabel}
              </Button>
            )}
            {onBack && (
              <Button variant="transparent" c="gray.6" onClick={onBack} size="sm">
                {backLabel}
              </Button>
            )}
          </Stack>
        </motion.div>
      </Stack>
    </motion.div>
  );
}
